import { isE164 } from "../telephony/sip-trunk/wire-number.js";
import { partyLabelTooLong } from "./setting.js";
/** How sure the access list is of who the party is (`party_access.assurance_level`, §40.7.1). */
export const PARTY_ACCESS_ASSURANCE_LEVEL = ["number_only", "number_sms_code", "number_passcode", "signed_visitor"];
const OPEN = Object.freeze({ kind: "open" });
const NOT_LISTED = Object.freeze({ kind: "not_listed" });
/**
 * Whether a list is enabled AND non-empty: at least one entry, or "any signed visitor" (§40.25.2). An enabled list
 * with neither admits nobody, and is not what lifts an internal VoiceAgent's lock.
 */
export function partyAccessListInForce(access, accessEntryPresent) {
    return access !== null && access.enabled && (accessEntryPresent || access.anySignedVisitor);
}
/** Which identity kinds a match may have on this leg: a trunk-local entry only on a trunk, a phone entry on any voice leg. */
function voiceMatchFits(leg, kind) {
    if (kind === "phone")
        return leg.kind === "carrier" || leg.kind === "sip_trunk";
    if (kind === "trunk_local")
        return leg.kind === "sip_trunk";
    return false;
}
/** The access list's decision for one party (§40.7.3 as amended by §40.25.2, and on 2026-10-28). Pure. */
export function partyAccessDecisionOf(input) {
    const { access, entryMatch, leg } = input;
    if (input.audience === "internal" && input.internalLockOn && !partyAccessListInForce(access, input.accessEntryPresent)) {
        return NOT_LISTED;
    }
    if (access === null || !access.enabled)
        return OPEN;
    const listed = (entry) => Object.freeze({ kind: "listed", entry, assuranceLevel: access.assuranceLevel });
    if (leg.kind === "web") {
        if (input.signedVisitor === null)
            return NOT_LISTED;
        // A web leg's match was made on the visitor's claims: its subject's entry, or its `phone` claim's digest.
        if (entryMatch !== null && (entryMatch.identityKind === "signed_visitor" || entryMatch.identityKind === "phone")) {
            return listed(entryMatch);
        }
        return access.anySignedVisitor ? listed("any_signed_visitor") : NOT_LISTED;
    }
    if (access.assuranceLevel === "signed_visitor")
        return NOT_LISTED;
    if (entryMatch !== null && voiceMatchFits(leg, entryMatch.identityKind))
        return listed(entryMatch);
    return NOT_LISTED;
}
/**
 * Whether a NEW binding of this VoiceAgent must be refused `422 access_list_required` (§40.19.4; D-12): an
 * internal-audience VoiceAgent whose access list is not enabled. The api asks only while the internal access lock is on
 * (`WETALK_API_INTERNAL_ACCESS_LOCK`, since 2026-10-28; before it, the portal's `lock_pending` warning stands in);
 * existing bindings are never touched.
 */
export function bindingNeedsAccessList(audience, access) {
    return audience === "internal" && (access === null || !access.enabled);
}
/**
 * Where a VoiceAgent stands with the internal access lock (`PartyAccess.lock_state`, design §40.25.2 as amended
 * 2026-10-28): `none` (not internal, its list in force, or the editor off), `pending` (the lock is coming: the portal's
 * `access.lock_pending`), `locked` (refusing every party: `access.locked`).
 */
export const INTERNAL_ACCESS_LOCK_STATE = ["none", "pending", "locked"];
/**
 * The lock state the api reports, the same rule as the derived notices: an internal-audience VoiceAgent with no enabled,
 * non-empty list is `locked` once the lock is on — or already while its list is enabled but empty, which refuses every
 * party whatever the switches say — and `pending` before that, while the editor is on. Pure.
 */
export function internalAccessLockStateOf(input) {
    if (input.audience !== "internal" || !input.editorOn)
        return "none";
    if (partyAccessListInForce(input.access, input.accessEntryPresent))
        return "none";
    return input.lockOn || (input.access !== null && input.access.enabled) ? "locked" : "pending";
}
// ---- entries (design §40.7.1, §40.19.3) --------------------------------------------------------------------------------
/** At most this many entries per VoiceAgent (`422 access_entry_limit_reached`), enforced in the writer under a lock. */
export const PARTY_ACCESS_ENTRY_MOST = 500;
/** A signed visitor's subject on an entry, at most this many characters (0187's CHECK). */
export const PARTY_ACCESS_SUBJECT_CHARACTER_MOST = 128;
/** A trunk-local number, at most this many characters (0187's CHECK). */
export const PARTY_ACCESS_TRUNK_LOCAL_CHARACTER_MOST = 64;
/** A passcode's digit count (Release 2; 0187's `passcode_digit_count` CHECK). */
export const PARTY_PASSCODE_DIGIT_RANGE = Object.freeze({ least: 4, most: 8 });
/** The `detail.code` of a refused entry (`400 validation_failed`, §40.19.3), in refusal order. */
export const PARTY_ACCESS_ENTRY_REFUSAL = ["label_too_long", "number_invalid", "subject_too_long"];
/** Any C0 or C1 control character, which no number or subject a person can type holds. */
function holdsControlCharacter(value) {
    for (const character of value) {
        const code = character.codePointAt(0);
        if (code <= 0x1f || (code >= 0x7f && code <= 0x9f))
            return true;
    }
    return false;
}
function characterCount(value) {
    return [...value].length;
}
/**
 * The first rule an entry breaks, or `null`. A phone identity is E.164 (the api normalised the typed number with its
 * region first; a number that would not normalise is `number_invalid` there too); a trunk-local number is 1–64
 * characters without control characters (`number_invalid`); a subject is at most 128 characters
 * (`subject_too_long`). An empty label or subject is a shape error the api refuses before this.
 */
export function partyAccessEntryRefusalOf(input) {
    if (partyLabelTooLong(input.label))
        return "label_too_long";
    const identity = input.identity;
    switch (identity.kind) {
        case "phone":
            return isE164(identity.e164) ? null : "number_invalid";
        case "trunk_local": {
            const count = characterCount(identity.trunkLocalNumber);
            if (count < 1 || count > PARTY_ACCESS_TRUNK_LOCAL_CHARACTER_MOST || holdsControlCharacter(identity.trunkLocalNumber)) {
                return "number_invalid";
            }
            return null;
        }
        case "signed_visitor":
            return characterCount(identity.signedVisitorSubject) > PARTY_ACCESS_SUBJECT_CHARACTER_MOST ? "subject_too_long" : null;
    }
}
//# sourceMappingURL=access.js.map